/**
 * strategy-signals-page.js
 *
 * Generic page component for displaying pre-computed strategy signals
 * grouped by timeframe. Used by CCI Pullback, MTF 50 SMA, Trend Pullback,
 * and TopDog pages.
 *
 * Props (config object):
 *   title          string   — page heading, e.g. "CCI Pullback"
 *   icon           string   — emoji icon
 *   subtitle       string   — one-line strategy description
 *   fetchFn        async fn — calls a server-api function, returns { results, lastUpdated }
 *   timeframes     string[] — ordered list of timeframe keys to display
 *   tfLabels       object   — { weekly: 'Weekly', daily: 'Daily', fourHour: '4H' }
 *   renderDetails  fn       — (entry) => string detail line shown under coin name
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../components/loader';
import ErrorBanner from '../components/error-banner';
import { formatPrice, formatPercentChange } from '../utils/format-helpers';
import { isServerApiAvailable } from '../services/server-api';
import './rsi-mtf-page.css';

// ─── Signal card ──────────────────────────────────────────────────────────────

function SignalCard({ entry, renderDetails }) {
  const navigate = useNavigate();
  const isLong = entry.state === 'long';

  return (
    <div
      className={`mtf-card ${entry.state}`}
      onClick={() => navigate(`/coin/${entry.id}`)}
      title="Click for details"
    >
      <div className="mtf-card-top">
        <div className="mtf-coin-identity">
          {entry.image && (
            <img src={entry.image} alt={entry.name} className="mtf-coin-logo"
              onError={e => (e.target.style.display = 'none')} />
          )}
          <div>
            <div className="mtf-coin-symbol">{entry.symbol?.toUpperCase()}</div>
            <div className="mtf-coin-name">{entry.name}</div>
          </div>
        </div>
        <div className={`mtf-direction-badge ${entry.state}`}>
          {isLong ? '▲ LONG' : '▼ SHORT'}
        </div>
      </div>

      {renderDetails && (
        <div className="mtf-indicators">
          {renderDetails(entry)}
        </div>
      )}

      <div className="mtf-card-footer">
        <div className="mtf-price-block">
          <span className="mtf-price">{formatPrice(entry.current_price || entry.currentPrice)}</span>
          <span className={`mtf-change ${(entry.price_change_percentage_24h || 0) >= 0 ? 'positive' : 'negative'}`}>
            {formatPercentChange(entry.price_change_percentage_24h)}
          </span>
        </div>
        <div className="mtf-rank">#{entry.marketCapRank || entry.market_cap_rank}</div>
      </div>
    </div>
  );
}

// ─── Timeframe section ────────────────────────────────────────────────────────

function TimeframeSection({ label, entries, renderDetails }) {
  const longs  = entries.filter(e => e.state === 'long');
  const shorts = entries.filter(e => e.state === 'short');

  if (entries.length === 0) return null;

  return (
    <div className="rb-section">
      <div className="rb-section-header">
        <div className="rb-section-title">
          <span>{label}</span>
          <span className="rb-section-count">{longs.length} long · {shorts.length} short</span>
        </div>
      </div>
      <div className="mtf-grid">
        {entries.map(e => (
          <SignalCard
            key={`${e.id}-${e.state}`}
            entry={e}
            renderDetails={renderDetails}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function StrategySignalsPage({ config }) {
  const { title, icon, subtitle, fetchFn, timeframes, tfLabels, renderDetails } = config;

  const [results, setResults]         = useState({});
  const [isLoading, setIsLoading]     = useState(true);
  const [error, setError]             = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (!isServerApiAvailable()) {
        setError('This strategy requires the VPS server. Start the server and refresh.');
        return;
      }
      const data = await fetchFn();
      // fetchFn returns { <key>: { weekly?, daily, fourHour }, lastUpdated }
      // Extract the first non-metadata key as the results object
      const resultsKey = Object.keys(data).find(k => k !== 'lastUpdated');
      setResults(data[resultsKey] || {});
      setLastUpdated(new Date(data.lastUpdated).getTime());
    } catch (err) {
      if (err.message === 'Data not yet available') {
        setError('Server is starting up. Please refresh in a moment.');
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchFn]);

  useEffect(() => { load(); }, [load]);

  // Filter across all timeframes
  const filterEntry = entry => {
    if (activeFilter === 'long')  return entry.state === 'long';
    if (activeFilter === 'short') return entry.state === 'short';
    return true;
  };

  const totalLong  = timeframes.reduce((n, tf) => n + (results[tf] || []).filter(e => e.state === 'long').length, 0);
  const totalShort = timeframes.reduce((n, tf) => n + (results[tf] || []).filter(e => e.state === 'short').length, 0);

  if (isLoading) {
    return (
      <div className="mtf-page">
        <div className="mtf-page-header">
          <h1 className="mtf-page-title">{icon} {title}</h1>
          <p className="mtf-page-subtitle">{subtitle}</p>
        </div>
        <div className="mtf-loading-center">
          <Loader size="large" text="Loading signals…" />
        </div>
      </div>
    );
  }

  return (
    <div className="mtf-page">
      <div className="mtf-page-header">
        <div className="mtf-header-main">
          <h1 className="mtf-page-title">{icon} {title}</h1>
          <p className="mtf-page-subtitle">{subtitle}</p>
        </div>
        <button className="mtf-refresh-btn" onClick={load}>↻ Refresh</button>
      </div>

      {error && (
        <ErrorBanner message={error} type="error" onDismiss={() => setError(null)} onRetry={load} />
      )}

      {/* Summary bar */}
      <div className="mtf-summary-bar">
        <div className="mtf-summary-stat">
          <span className="mtf-summary-number">{totalLong + totalShort}</span>
          <span className="mtf-summary-label">Total Signals</span>
        </div>
        <div className="mtf-summary-stat long-stat">
          <span className="mtf-summary-number">{totalLong}</span>
          <span className="mtf-summary-label">▲ Long</span>
        </div>
        <div className="mtf-summary-stat short-stat">
          <span className="mtf-summary-number">{totalShort}</span>
          <span className="mtf-summary-label">▼ Short</span>
        </div>
        {lastUpdated && (
          <div className="mtf-summary-updated">
            Updated {new Date(lastUpdated).toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className="mtf-filter-tabs">
        {['all', 'long', 'short'].map(f => (
          <button
            key={f}
            className={`mtf-filter-tab ${activeFilter === f ? 'active' : ''} ${f !== 'all' ? f : ''}`}
            onClick={() => setActiveFilter(f)}
          >
            {f === 'all' ? '⬛ All' : f === 'long' ? '▲ Long only' : '▼ Short only'}
          </button>
        ))}
      </div>

      {/* Results by timeframe */}
      {(totalLong + totalShort) === 0 ? (
        <div className="mtf-empty">
          <div className="mtf-empty-icon">🔍</div>
          <div className="mtf-empty-text">No {title} signals right now.</div>
          <div className="mtf-empty-sub">All conditions must align simultaneously — check back after the next hourly refresh.</div>
        </div>
      ) : (
        timeframes.map(tf => {
          const filtered = (results[tf] || []).filter(filterEntry);
          return filtered.length > 0 ? (
            <TimeframeSection
              key={tf}
              label={tfLabels[tf] || tf}
              entries={filtered}
              renderDetails={renderDetails}
            />
          ) : null;
        })
      )}
    </div>
  );
}

export default StrategySignalsPage;
